<!--
 * @Author       : AI
 * @Date         : 2026-06-29
 * @FilePath     : /ai-agent-lumina/src/components/global/loading/src/izt-loading.vue
 * @Description  : 加载中组件
 *
 * 显示状态 visible
 *  - false 默认隐藏
 *  - true 显示
 * 加载文案 text
 *  - 自定义加载中提示文案，默认 '加载中...'
 * 是否全屏 overlay
 *  - true 全屏遮罩 (默认)
 *  - false 内联显示
 * 加载图标类型 type
 *  - dots 跳动圆点 (默认)
 *  - ring 光环旋转
 *  - pulse 脉冲呼吸
 *  - gif 使用think.gif动图
 * 是否显示卡片 card
 *  - true 毛玻璃卡片包裹 (默认)
 *  - false 无卡片背景
 * 自定义类名 className
-->
<style lang="scss" scoped>
.izt-loading {
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: var(--izt-background-color, rgba(255, 255, 255, 1));
  .loading-card {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 16px;
    flex-direction: column;
  }

  // 全屏遮罩模式
  &.overlay {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    z-index: 9999;
  }

  // 内联模式
  &.inline {
    position: relative;
    padding: 60px 0;
    width: 100%;
  }
}

// ====== 动画区域 ======
.loading-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 80px;
  height: 80px;
  position: relative;

  .think-gif {
    width: 80px;
    height: 80px;
    object-fit: contain;
    border-radius: 50%;
  }
}

// --- 跳动圆点 ---
.dots-wrapper {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;

  .dot {
    width: 16px;
    height: 16px;
    border-radius: 50%;
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
    animation: dotBounce 1.4s ease-in-out infinite both;

    &:nth-child(1) { animation-delay: 0s; }
    &:nth-child(2) { animation-delay: 0.16s; }
    &:nth-child(3) { animation-delay: 0.32s; }
  }
}

@keyframes dotBounce {
  0%, 80%, 100% {
    transform: scale(0.6);
    opacity: 0.4;
  }
  40% {
    transform: scale(1);
    opacity: 1;
  }
}

// --- 光环旋转 ---
.ring-wrapper {
  width: 56px;
  height: 56px;
  position: relative;

  .ring {
    width: 100%;
    height: 100%;
    border-radius: 50%;
    border: 4px solid transparent;
    border-top-color: #667eea;
    border-right-color: #764ba2;
    animation: ringSpin 1s linear infinite;
    position: absolute;
    top: 0;
    left: 0;

    &:nth-child(2) {
      width: 70%;
      height: 70%;
      top: 15%;
      left: 15%;
      border-top-color: #f093fb;
      border-right-color: #f5576c;
      animation-direction: reverse;
      animation-duration: 0.8s;
    }
  }
}

@keyframes ringSpin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

// --- 脉冲呼吸 ---
.pulse-wrapper {
  width: 56px;
  height: 56px;
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;

  .pulse-core {
    width: 20px;
    height: 20px;
    border-radius: 50%;
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
    position: relative;
    z-index: 2;
  }

  .pulse-ring {
    position: absolute;
    width: 100%;
    height: 100%;
    border-radius: 50%;
    border: 2px solid #667eea;
    animation: pulseExpand 1.8s ease-out infinite;

    &:nth-child(2) {
      animation-delay: 0.6s;
    }
    &:nth-child(3) {
      animation-delay: 1.2s;
    }
  }
}

@keyframes pulseExpand {
  0% {
    transform: scale(0.3);
    opacity: 1;
  }
  100% {
    transform: scale(1);
    opacity: 0;
  }
}

// ====== 文案 ======
.loading-text {
  font-size: 28px;
  color: #555;
  text-align: center;
  line-height: 1.5;
  letter-spacing: 1px;
  font-weight: 500;

  .text-dot {
    display: inline-block;
    animation: textDotFade 1.4s ease-in-out infinite;

    &:nth-child(1) { animation-delay: 0s; }
    &:nth-child(2) { animation-delay: 0.2s; }
    &:nth-child(3) { animation-delay: 0.4s; }
  }
}

@keyframes textDotFade {
  0%, 80%, 100% { opacity: 0; }
  40% { opacity: 1; }
}

// ====== 过渡动画 ======
.loading-fade-enter-active {
  transition: opacity 0.3s ease;
}

.loading-fade-leave-active {
  transition: opacity 0.25s ease;
}

.loading-fade-enter-from {
  opacity: 0;
}

.loading-fade-leave-to {
  opacity: 0;
}
</style>

<template>
  <Teleport to="body" v-if="overlay">
    <Transition name="loading-fade">
      <div
        v-if="visible"
        :class="['izt-loading', 'overlay', className]"
        @click.stop
      >
        <div :class="['loading-card', { 'no-card': !card }]">
          <div class="loading-icon">
            <template v-if="type === 'gif'">
              <img class="think-gif" src="@/assets/img/think.gif" alt="loading" />
            </template>
            <template v-else-if="type === 'ring'">
              <div class="ring-wrapper">
                <div class="ring"></div>
                <div class="ring"></div>
              </div>
            </template>
            <template v-else-if="type === 'pulse'">
              <div class="pulse-wrapper">
                <div class="pulse-core"></div>
                <div class="pulse-ring"></div>
                <div class="pulse-ring"></div>
                <div class="pulse-ring"></div>
              </div>
            </template>
            <template v-else>
              <div class="dots-wrapper">
                <div class="dot"></div>
                <div class="dot"></div>
                <div class="dot"></div>
              </div>
            </template>
          </div>
          <span class="loading-text">
            <template v-if="showAnimatedDots">
              {{ textWithoutDots }}<span class="text-dot">.</span><span class="text-dot">.</span><span class="text-dot">.</span>
            </template>
            <template v-else>{{ text }}</template>
          </span>
        </div>
      </div>
    </Transition>
  </Teleport>
  <Transition name="loading-fade" v-else>
    <div
      v-if="visible"
      :class="['izt-loading', 'inline', className]"
    >
      <div :class="['loading-card', { 'no-card': !card }]">
        <div class="loading-icon">
          <template v-if="type === 'gif'">
            <img class="think-gif" src="@/assets/img/think.gif" alt="loading" />
          </template>
          <template v-else-if="type === 'ring'">
            <div class="ring-wrapper">
              <div class="ring"></div>
              <div class="ring"></div>
            </div>
          </template>
          <template v-else-if="type === 'pulse'">
            <div class="pulse-wrapper">
              <div class="pulse-core"></div>
              <div class="pulse-ring"></div>
              <div class="pulse-ring"></div>
              <div class="pulse-ring"></div>
            </div>
          </template>
          <template v-else>
            <div class="dots-wrapper">
              <div class="dot"></div>
              <div class="dot"></div>
              <div class="dot"></div>
            </div>
          </template>
        </div>
        <span class="loading-text">
          <template v-if="showAnimatedDots">
            {{ textWithoutDots }}<span class="text-dot">.</span><span class="text-dot">.</span><span class="text-dot">.</span>
          </template>
          <template v-else>{{ text }}</template>
        </span>
      </div>
    </div>
  </Transition>
</template>

<script setup name="izt-loading">
  import { computed } from 'vue'
  import { useI18n } from 'vue-i18n'
  defineOptions({ name: 'izt-loading' });

  const { t } = useI18n();
  const props = defineProps({
    // 是否显示
    visible: {
      type: Boolean,
      default: false
    },
    // 加载文案
    text: {
      type: String,
      default: null
    },
    // 是否全屏遮罩
    overlay: {
      type: Boolean,
      default: true
    },
    // 加载动画类型: dots | ring | pulse | gif
    type: {
      type: String,
      default: 'dots'
    },
    // 是否显示毛玻璃卡片
    card: {
      type: Boolean,
      default: false
    },
    // 自定义类名
    className: {
      type: String,
      default: ''
    }
  });

  // 如果文案以 ... 结尾，自动替换为动态跳动省略号
  const showAnimatedDots = computed(() => (props.text || t('common.加载中')).endsWith('...'));
  const textWithoutDots = computed(() => (props.text || t('common.加载中')).replace(/\.{3}$/, ''));
</script>
